import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Plus, Trash2, Loader2, ChevronDown, ChevronUp, Beaker, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { Field } from "./shared";

export default function AnalyseSolTab({ parcelId, refData, ilotId, ilots = [] }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/analyses-sol?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error('[AnalyseSolTab] fetch failed:', err); }
    setLoading(false);
  }, [parcelId, ilotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await authFetch(`${API_BASE}/carnet/analyses-sol/${id}`, { method: "DELETE" });
      toast.success("Analyse supprimee"); setDeletingId(null); fetchData();
    } catch { toast.error("Erreur"); setDeletingId(null); }
  };

  const handleUploadPdf = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) { toast.error("PDF trop volumineux (max 20 Mo)"); return; }
      setUploading(true);
      toast.info("Extraction IA en cours...");
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("parcel_id", parcelId);
        if (ilotId) fd.append("ilot_id", ilotId);
        const res = await authFetch(`${API_BASE}/carnet/analyses-sol/extract-pdf`, { method: "POST", body: fd });
        if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
        toast.success("Analyse extraite du PDF avec succes");
        fetchData();
      } catch (e) { toast.error(e.message || "Erreur d'extraction"); }
      setUploading(false);
    };
    input.click();
  };

  if (showForm) {
    return <AnalyseSolForm parcelId={parcelId} ilotId={ilotId} refData={refData}
      onSaved={() => { setShowForm(false); fetchData(); }}
      onCancel={() => setShowForm(false)} />;
  }

  const SOL_PARAMS = [
    ["pH eau", "ph_eau"], ["pH KCl", "ph_kcl"], ["Mat. organique (%)", "matiere_organique"],
    ["Azote total", "azote_total"], ["P Olsen (mg/kg)", "phosphore_olsen"],
    ["K echang.", "potassium_echangeable"], ["Ca echang.", "calcium_echangeable"],
    ["Mg echang.", "magnesium_echangeable"], ["CEC", "cec"], ["C organique", "carbone_organique"],
    ["C/N", "rapport_cn"], ["Conductivite", "conductivite"],
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5 flex gap-2">
        <Button size="sm" className="flex-1 h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5"
          style={{ background: "#6366F1" }}
          onClick={() => setShowForm(true)} data-testid="sol-new-btn">
          <Plus className="w-3.5 h-3.5" /> Saisie manuelle
        </Button>
        <Button size="sm" variant="outline" className="h-9 text-[11px] font-semibold rounded-lg gap-1.5 border-black/10"
          style={{ color: "#6366F1" }}
          onClick={handleUploadPdf} disabled={uploading} data-testid="sol-upload-pdf-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5" /> PDF + IA</>}
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#6366F1" }} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Beaker className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucune analyse de sol</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {items.map(item => {
              const expanded = expandedId === item.analyse_id;
              return (
                <div key={item.analyse_id} className="rounded-xl border border-black/5 overflow-hidden" data-testid={`sol-${item.analyse_id}`}>
                  <button onClick={() => setExpandedId(expanded ? null : item.analyse_id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/[0.02]">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#6366F115" }}>
                      <Beaker className="w-4 h-4" style={{ color: "#6366F1" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>Analyse {item.year}</p>
                        {item.ai_extracted && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#6366F115", color: "#6366F1" }}>IA</Badge>}
                        {item.ilot_id && ilotName(item.ilot_id) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(item.ilot_id)}</Badge>}
                      </div>
                      <p className="text-[10px]" style={{ color: "#7A7A7A" }}>{item.laboratoire || "Non precise"} - {item.date_prelevement}</p>
                    </div>
                    {expanded ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 border-t border-black/5 pt-2">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {SOL_PARAMS.filter(([, key]) => item[key] != null).map(([label, key]) => (
                          <div key={key} className="flex justify-between text-[11px] py-0.5">
                            <span style={{ color: "#7A7A7A" }}>{label}</span>
                            <span className="font-medium" style={{ color: "#1A1A1A" }}>{item[key]}</span>
                          </div>
                        ))}
                      </div>
                      {item.notes && <p className="text-[10px] mt-2 italic" style={{ color: "#7A7A7A" }}>{item.notes}</p>}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-black/5">
                        {deletingId === item.analyse_id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Supprimer ?</span>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(item.analyse_id)} data-testid="confirm-delete-btn">Oui, supprimer</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setDeletingId(null)}>Annuler</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg text-red-500"
                            onClick={() => setDeletingId(item.analyse_id)}>
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function AnalyseSolForm({ parcelId, ilotId, refData, onSaved, onCancel }) {
  const [form, setForm] = useState({
    date_prelevement: new Date().toISOString().slice(0, 10),
    laboratoire: "", ph_eau: "", ph_kcl: "", matiere_organique: "",
    azote_total: "", phosphore_olsen: "", potassium_echangeable: "",
    calcium_echangeable: "", magnesium_echangeable: "", cec: "",
    conductivite: "", carbone_organique: "", rapport_cn: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { parcel_id: parcelId, ilot_id: ilotId || null, ...form };
      // Convert string numbers to floats
      for (const key of ["ph_eau", "ph_kcl", "matiere_organique", "azote_total", "phosphore_olsen",
        "potassium_echangeable", "calcium_echangeable", "magnesium_echangeable", "cec",
        "conductivite", "carbone_organique", "rapport_cn"]) {
        payload[key] = payload[key] ? parseFloat(payload[key]) : null;
      }
      const res = await authFetch(`${API_BASE}/carnet/analyses-sol`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success("Analyse enregistree");
      onSaved();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setSaving(false);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3" data-testid="sol-form">
        <div className="flex items-center gap-2 pb-2 border-b border-black/5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}><ArrowLeft className="w-4 h-4" /></Button>
          <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Nouvelle analyse de sol</h3>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date prelevement" value={form.date_prelevement} onChange={v => set("date_prelevement", v)} type="date" required testId="sol-date" />
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Laboratoire</Label>
            <Select value={form.laboratoire} onValueChange={v => set("laboratoire", v)}>
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="sol-lab"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent className="z-[9999]">
                {(refData?.laboratoires || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="p-3 rounded-lg" style={{ background: "#6366F108" }}>
          <p className="text-[11px] font-bold mb-2" style={{ color: "#6366F1" }}>Parametres physico-chimiques</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="pH eau" value={form.ph_eau} onChange={v => set("ph_eau", v)} type="number" testId="sol-ph-eau" />
            <Field label="pH KCl" value={form.ph_kcl} onChange={v => set("ph_kcl", v)} type="number" testId="sol-ph-kcl" />
            <Field label="Mat. organique (%)" value={form.matiere_organique} onChange={v => set("matiere_organique", v)} type="number" testId="sol-mo" />
            <Field label="Azote total" value={form.azote_total} onChange={v => set("azote_total", v)} type="number" testId="sol-azote" />
            <Field label="C organique" value={form.carbone_organique} onChange={v => set("carbone_organique", v)} type="number" testId="sol-c" />
            <Field label="C/N" value={form.rapport_cn} onChange={v => set("rapport_cn", v)} type="number" testId="sol-cn" />
          </div>
        </div>

        <div className="p-3 rounded-lg" style={{ background: "#8B5E3C08" }}>
          <p className="text-[11px] font-bold mb-2" style={{ color: "#8B5E3C" }}>Elements echangeables</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="P Olsen (mg/kg)" value={form.phosphore_olsen} onChange={v => set("phosphore_olsen", v)} type="number" testId="sol-p" />
            <Field label="K echang." value={form.potassium_echangeable} onChange={v => set("potassium_echangeable", v)} type="number" testId="sol-k" />
            <Field label="Ca echang." value={form.calcium_echangeable} onChange={v => set("calcium_echangeable", v)} type="number" testId="sol-ca" />
            <Field label="Mg echang." value={form.magnesium_echangeable} onChange={v => set("magnesium_echangeable", v)} type="number" testId="sol-mg" />
            <Field label="CEC" value={form.cec} onChange={v => set("cec", v)} type="number" testId="sol-cec" />
            <Field label="Conductivite" value={form.conductivite} onChange={v => set("conductivite", v)} type="number" testId="sol-cond" />
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Notes</Label>
          <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Observations..."
            className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="sol-notes" />
        </div>

        <Button onClick={handleSubmit} disabled={saving}
          className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#6366F1" }}
          data-testid="sol-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Enregistrer l'analyse
        </Button>
      </div>
    </ScrollArea>
  );
}

